(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, amount) => from + ((to - from) * amount);
  const wait = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));

  const initJuUsage = () => {
    document.querySelectorAll('.holder-usage-story--ju[data-holder-usage-story]').forEach((section) => {
      if (section.dataset.juEnhancedReady === 'true') return;
      section.dataset.juEnhancedReady = 'true';
      // Claims the section so the legacy inline controller leaves it alone.
      section.dataset.usageReady = 'true';

      const visual = section.querySelector('.holder-usage-story__visual');
      const mediaVideos = [...section.querySelectorAll('.holder-usage-story__video')];
      let video = section.querySelector('[data-usage-primary-video]') || mediaVideos[0] || section.querySelector('video');
      const details = [...section.querySelectorAll('[data-usage-detail]')];
      const scenes = [...section.querySelectorAll('[data-usage-scene]')];
      const previousButton = section.querySelector('[data-usage-previous]');
      const nextButton = section.querySelector('[data-usage-next]');
      const currentStepLabel = section.querySelector('[data-usage-current]');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!visual || !video) return;

      const fallbackDuration = 18.37;
      // 1.34 is where the tallest frame (exploded stack, 74.4% of the frame) would
      // touch both edges; 1.25 keeps a margin so no part of the product is cropped.
      const zoomedScale = 1.25;
      const zoomedPanY = 0;
      // Each holder ships its own cut, so step boundaries ride on the element.
      const defaultBounds = [[0, 1.54], [1.54, 8.63], [8.63, 10.25], [10.25, 11.03], [11.03, 18.37], [0, 8.63]];
      const readBounds = (element) => {
        const raw = element?.dataset.usageSteps;
        if (!raw) return defaultBounds;
        const parsed = raw.split('|').map((pair) => pair.split(',').map(Number));
        const usable = parsed.length > 0 && parsed.every((p) => p.length === 2 && p.every(Number.isFinite));
        return usable ? parsed : defaultBounds;
      };
      // The framing is fixed now: one zoom level for the whole sequence, so no
      // step animates the transform — it only ever holds `zoomedScale`.
      const buildSegments = (element) => {
        const bounds = readBounds(element);
        const built = {};
        bounds.forEach(([start, end], index) => {
          built[index + 1] = {
            start, end,
            transformStart: start, transformEnd: end,
            zoomStart: zoomedScale, zoomEnd: zoomedScale,
            panStart: zoomedPanY, panEnd: zoomedPanY
          };
        });
        return built;
      };
      let segments = buildSegments(video);
      let stepCount = Object.keys(segments).length;

      const isNarrow = () => window.matchMedia('(max-width: 749px)').matches;

      const koreanUi = (document.documentElement.lang || '').toLowerCase().startsWith('ko');


      let currentStep = 0;
      let dots = [];
      let hintLabel = null;
      let playToken = 0;
      let animationFrame = 0;
      let videoDuration = fallbackDuration;
      let pointerStart = null;

      const setTransformAtTime = (segment, time) => {
        const span = Math.max(.01, segment.transformEnd - segment.transformStart);
        const amount = clamp((time - segment.transformStart) / span, 0, 1);
        visual.style.setProperty('--usage-zoom', lerp(segment.zoomStart, segment.zoomEnd, amount).toFixed(4));
        const panAmount = Number.isFinite(segment.panMid)
          ? (amount <= .5
            ? lerp(segment.panStart, segment.panMid, amount * 2)
            : lerp(segment.panMid, segment.panEnd, (amount - .5) * 2))
          : lerp(segment.panStart, segment.panEnd, amount);
        visual.style.setProperty('--usage-pan-y', `${panAmount.toFixed(3)}%`);
      };

      const cancelPlayback = () => {
        playToken += 1;
        window.cancelAnimationFrame(animationFrame);
        animationFrame = 0;
        video.pause();
      };

      const seekVideo = (time, token) => new Promise((resolve) => {
        const target = clamp(time, 0, Math.max(0, videoDuration - .02));
        if (token !== playToken) return resolve(false);
        if (video.readyState >= 2 && Math.abs(video.currentTime - target) < .04) return resolve(true);

        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          video.removeEventListener('seeked', finish);
          resolve(token === playToken);
        };
        video.addEventListener('seeked', finish, { once: true });
        try {
          video.currentTime = target;
        } catch (error) {
          finish();
        }
        window.setTimeout(finish, 500);
      });

      const updatePanels = (step) => {
        currentStep = clamp(step, 0, stepCount);
        section.classList.add('is-expanded', 'is-mobile-viewer');
        section.classList.toggle('is-ju-initial', currentStep === 0);
        section.classList.remove('is-ju-monochrome', 'is-ju-fading', 'is-mobile-switching');
        section.classList.toggle('has-active-step', currentStep > 0);
        section.classList.toggle('is-mobile-ready', currentStep > 0);
        section.classList.remove('is-mobile-switching');
        visual.classList.add('is-frame-ready');

        details.forEach((detail) => {
          const active = currentStep > 0 && Number(detail.dataset.usageDetail) === currentStep;
          detail.classList.toggle('is-active', active);
          detail.hidden = !active;
        });
        scenes.forEach((scene) => {
          const active = currentStep > 0 && Number(scene.dataset.usageScene) === currentStep;
          scene.classList.toggle('is-active', active);
          scene.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
        if (currentStepLabel) currentStepLabel.textContent = `${String(currentStep).padStart(2, '0')} / ${String(stepCount).padStart(2, '0')}`;
        dots.forEach((dot) => {
          const active = Number(dot.dataset.usageDot) === currentStep;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'step');
          else dot.removeAttribute('aria-current');
        });
        if (previousButton) previousButton.disabled = currentStep === 0;
        if (nextButton) nextButton.disabled = false;
        const prevZoneEl = visual.querySelector('[data-usage-zone="prev"]');
        const nextZoneEl = visual.querySelector('[data-usage-zone="next"]');
        if (prevZoneEl) prevZoneEl.disabled = currentStep <= 1;
        if (nextZoneEl) nextZoneEl.disabled = false;
        section.classList.toggle('is-last-step', currentStep === stepCount);
        for (let n = 1; n <= stepCount; n += 1) section.classList.toggle(`is-step-${n}`, currentStep === n);
        section.classList.remove('is-step-complete');
        if (hintLabel) {
          hintLabel.textContent = currentStep === 0
            ? (koreanUi ? '왼쪽으로 밀어보세요' : 'Swipe left to start')
            : currentStep === stepCount
              ? (koreanUi ? '왼쪽으로 밀어 처음 단계로' : 'Swipe left to return to step one')
              : (koreanUi ? '왼쪽으로 밀어 다음 단계' : 'Swipe left for the next step');
        }
      };

      const playSegment = async (step, previousStep) => {
        const segment = segments[step];
        if (!segment) return;
        cancelPlayback();
        const token = playToken;
        const endTime = clamp(segment.end, 0, Math.max(0, videoDuration - .02));
        const seamlessForward = previousStep > 0 && step === previousStep + 1 && Math.abs(video.currentTime - segment.start) < .12;

        section.classList.remove('is-step-complete');
        const entryTime = segment.reverse ? endTime : segment.start;
        setTransformAtTime(segment, entryTime);
        const didSeek = await seekVideo(entryTime, token);
        if (!didSeek || token !== playToken) return;

        if (reduceMotion.matches) {
          await seekVideo(endTime, token);
          setTransformAtTime(segment, endTime);
          section.classList.add('is-step-complete');
          return;
        }

        if (segment.reverse) {
          const span = Math.max(.01, endTime - segment.start);
          video.pause();
          const startedAt = performance.now();
          const rewind = (now) => {
            if (token !== playToken) return;
            const amount = clamp((now - startedAt) / (span * 1000), 0, 1);
            const time = endTime - (span * amount);
            video.currentTime = time;
            setTransformAtTime(segment, time);
            if (amount >= 1) {
              section.classList.add('is-step-complete');
              return;
            }
            animationFrame = window.requestAnimationFrame(rewind);
          };
          animationFrame = window.requestAnimationFrame(rewind);
          return;
        }

        video.playbackRate = 1;
        try {
          await video.play();
        } catch (error) {
          return;
        }

        const monitor = () => {
          if (token !== playToken) return;
          setTransformAtTime(segment, video.currentTime);
          if (video.currentTime >= endTime - .035) {
            video.pause();
            video.currentTime = endTime;
            setTransformAtTime(segment, endTime);
            section.classList.add('is-step-complete');
            return;
          }
          animationFrame = window.requestAnimationFrame(monitor);
        };
        animationFrame = window.requestAnimationFrame(monitor);
      };

      const selectStep = (step) => {
        const nextStep = clamp(step, 0, stepCount);
        const previousStep = currentStep;
        const stableScrollY = window.scrollY;
        updatePanels(nextStep);
        if (window.matchMedia('(max-width: 749px)').matches) {
          window.scrollTo({ top: stableScrollY, left: 0, behavior: 'instant' });
          window.requestAnimationFrame(() => {
            window.scrollTo({ top: stableScrollY, left: 0, behavior: 'instant' });
          });
        }
        if (nextStep === 0) {
          cancelPlayback();
          setTransformAtTime(segments[1], segments[1].start);
          seekVideo(segments[1].start, playToken);
          return;
        }
        playSegment(nextStep, previousStep);
      };

      const steppedTarget = (direction) => {
        if (direction > 0 && currentStep === stepCount) return 1;
        return currentStep + direction;
      };

      const interceptStepButton = (button, direction) => {
        button?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          selectStep(steppedTarget(direction));
        }, { capture: true });
      };
      interceptStepButton(previousButton, -1);
      interceptStepButton(nextButton, 1);

      // Dot pagination: the active step reads as a dash, the rest as dots.
      const pager = section.querySelector('.holder-usage-story__mobile-pager');
      if (pager && !pager.querySelector('[data-usage-dots]')) {
        const dotWrap = document.createElement('div');
        dotWrap.className = 'holder-usage-story__dots';
        dotWrap.setAttribute('data-usage-dots', '');
        for (let index = 1; index <= stepCount; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'holder-usage-story__dot';
          dot.dataset.usageDot = String(index);
          dot.setAttribute('aria-label', `${index} / ${stepCount}`);
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            selectStep(index);
          }, { capture: true });
          dotWrap.appendChild(dot);
        }
        pager.appendChild(dotWrap);
        dots = [...dotWrap.children];
      }

      if (!visual.querySelector('[data-usage-swipe-hint]')) {
        const hint = document.createElement('p');
        hint.className = 'holder-usage-story__swipe-hint';
        hint.setAttribute('data-usage-swipe-hint', '');
        hint.setAttribute('aria-hidden', 'true');
        hint.innerHTML = '<span class="holder-usage-story__swipe-hint-arrow">←</span>'
          + `<span data-usage-hint-label>${koreanUi ? '왼쪽으로 밀어 다음 단계' : 'Swipe left for the next step'}</span>`;
        visual.appendChild(hint);
      }
      hintLabel = visual.querySelector('[data-usage-hint-label]');

      // Desktop: the halves of the media act as previous / next.
      if (!visual.querySelector('[data-usage-zone]')) {
        [['prev', -1], ['next', 1]].forEach(([name, direction]) => {
          const zone = document.createElement('button');
          zone.type = 'button';
          zone.className = `holder-usage-story__zone holder-usage-story__zone--${name}`;
          zone.dataset.usageZone = name;
          zone.setAttribute('aria-label', direction < 0
            ? (koreanUi ? '이전 단계' : 'Previous step')
            : (koreanUi ? '다음 단계' : 'Next step'));
          zone.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            selectStep(steppedTarget(direction));
          }, { capture: true });
          visual.appendChild(zone);
        });
      }

      const mediaTabs = [...section.querySelectorAll('[data-usage-media-tab]')];
      const selectMedia = (handle) => {
        const nextVideo = mediaVideos.find((item) => item.dataset.usageMedia === handle);
        if (!nextVideo || nextVideo === video) return;
        cancelPlayback();
        mediaVideos.forEach((item) => {
          item.pause();
          item.classList.toggle('is-active-media', item === nextVideo);
        });
        mediaTabs.forEach((tab) => {
          const active = tab.dataset.usageMediaTab === handle;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        video = nextVideo;
        segments = buildSegments(video);
        videoDuration = Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : fallbackDuration;
        stepCount = Object.keys(segments).length;
        video.preload = 'auto';
        if (video.readyState < 2) video.load();
        video.addEventListener('loadedmetadata', () => {
          if (video !== nextVideo) return;
          if (Number.isFinite(video.duration) && video.duration > 0) videoDuration = video.duration;
        }, { once: true });
        selectStep(isNarrow() ? 0 : 1);
      };
      mediaTabs.forEach((tab) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          selectMedia(tab.dataset.usageMediaTab);
        }, { capture: true });
      });

      // Swiping only over the video was easy to miss; accept it across the block.
      const swipeArea = section.querySelector('.holder-usage-story__layout') || visual;
      swipeArea.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
        pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, { passive: true });
      swipeArea.addEventListener('pointerup', (event) => {
        if (!pointerStart || pointerStart.id !== event.pointerId) return;
        const deltaX = event.clientX - pointerStart.x;
        const deltaY = event.clientY - pointerStart.y;
        pointerStart = null;
        const threshold = Math.max(48, visual.clientWidth * .08);
        if (Math.abs(deltaX) < threshold || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) return;
        selectStep(steppedTarget(deltaX < 0 ? 1 : -1));
      }, { passive: true });
      swipeArea.addEventListener('pointercancel', () => {
        pointerStart = null;
      }, { passive: true });

      const handleTimeUpdate = (event) => {
        if (event.target !== video) return;
        const segment = segments[currentStep];
        if (!segment || video.paused) return;
        const endTime = clamp(segment.end, 0, Math.max(0, videoDuration - .02));
        if (video.currentTime < endTime - .01) return;
        video.pause();
        video.currentTime = endTime;
        setTransformAtTime(segment, endTime);
        section.classList.add('is-step-complete');
      };

      // Every clip needs the same playback defaults, not just the first one.
      (mediaVideos.length ? mediaVideos : [video]).forEach((item) => {
        item.muted = true;
        item.defaultMuted = true;
        item.playsInline = true;
        item.loop = false;
        item.addEventListener('timeupdate', handleTimeUpdate);
      });

      const fitMobileViewport = () => {
        if (!window.matchMedia('(max-width: 749px)').matches) {
          section.style.removeProperty('--usage-viewport-height');
          section.style.removeProperty('--usage-header-height');
          return;
        }
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const header = document.querySelector('.o-header-group');
        const headerHeight = Math.max(0, header?.getBoundingClientRect().height || 0);
        const bottomCandidates = [
          document.querySelector('.o-product__actions-bar'),
          document.querySelector('#PBarNextFrameWrapper')
        ].filter(Boolean);
        const bottomHeight = bottomCandidates.reduce((largest, element) => {
          const rect = element.getBoundingClientRect();
          const style = window.getComputedStyle(element);
          if (style.display === 'none' || style.visibility === 'hidden' || rect.height <= 0) return largest;
          if (rect.bottom < viewportHeight - 2 || rect.top >= viewportHeight) return largest;
          return Math.max(largest, viewportHeight - Math.max(0, rect.top));
        }, 0);
        const availableHeight = Math.max(420, Math.floor(viewportHeight - bottomHeight));
        section.style.setProperty('--usage-header-height', `${Math.round(headerHeight)}px`);
        section.style.setProperty('--usage-viewport-height', `${availableHeight}px`);
      };

      fitMobileViewport();
      window.addEventListener('resize', fitMobileViewport, { passive: true });
      window.visualViewport?.addEventListener('resize', fitMobileViewport, { passive: true });
      video.addEventListener('loadedmetadata', () => {
        if (Number.isFinite(video.duration) && video.duration > 0) videoDuration = video.duration;
        if (currentStep === 0) video.currentTime = segments[1].start;
      });
      if (video.readyState >= 1 && Number.isFinite(video.duration)) videoDuration = video.duration;
      // Set the same initial layout immediately; prepare the original video
      // shortly before this section can be seen, rather than at page entry.
      updatePanels(isNarrow() ? 0 : 1);
      setTransformAtTime(segments[1], segments[1].start);
      const initialVideo = video;
      let initialMediaPrepared = false;
      const prepareInitialMedia = () => {
        if (initialMediaPrepared) return;
        initialMediaPrepared = true;
        if (video !== initialVideo || currentStep !== (isNarrow() ? 0 : 1)) return;
        video.preload = 'auto';
      if (video.readyState < 2) video.load();
      if (isNarrow()) {
        updatePanels(0);
        setTransformAtTime(segments[1], segments[1].start);
        seekVideo(segments[1].start, playToken);
        video.addEventListener('loadedmetadata', () => {
          if (currentStep !== 0) return;
          setTransformAtTime(segments[1], segments[1].start);
          seekVideo(segments[1].start, playToken);
        }, { once: true });
      } else {
        updatePanels(1);
        const showFirstFrame = () => {
          setTransformAtTime(segments[1], segments[1].start);
          seekVideo(segments[1].start, playToken);
        };
        showFirstFrame();
        // The first seek is a no-op until metadata lands, so repeat it then.
        video.addEventListener('loadedmetadata', showFirstFrame, { once: true });
        if ('IntersectionObserver' in window) {
          const playObserver = new IntersectionObserver((entries, observer) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            if (currentStep === 1) playSegment(1, 0);
          }, { threshold: .35 });
          playObserver.observe(section);
        }
      }
      };
      if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
        const mediaObserver = new IntersectionObserver((entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          prepareInitialMedia();
        }, {rootMargin:'600px 0px', threshold:0});
        mediaObserver.observe(visual);
        section.addEventListener('pointerdown', prepareInitialMedia, {once:true, passive:true});
        section.addEventListener('focusin', prepareInitialMedia, {once:true});
      } else {
        prepareInitialMedia();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initJuUsage, { once: true });
  } else {
    initJuUsage();
  }
})();
